// ====================== BLOCK 1/2 ======================
// src/pages/admin/PaymentRequests.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, FileText } from "lucide-react";
import api from "../../lib/api";
import { getToken, openAuthenticatedUrl } from "../../lib/auth";
import PaymentStatusPill from "../../components/admin/PaymentStatusPill";

type Row = {
  id: number;
  created_at: string;
  payment_completed_at?: string | null;

  /** 내부 상태(requested/approved/complete/failed/rejected...) */
  status: string;

  /** 화면용 승인 상태(승인대기/결제대기/결제완료/송금완료/요청철회/거절) */
  approval_status?: string | null;

  /** ✅ 상대방 예금주(거래상대 수취인) */
  counterparty_name?: string | null;

  category?: string | null;
  applicant_name?: string | null;
  applicant_phone?: string | null;
  email?: string | null;
  amount?: number | null;
  split_count?: number | null;
};

type MonthlyAgg = {
  ym: string; // YYYY-MM (KST)
  pay_total: number; // 결제완료 총액
  pay_cnt: number;
  send_total: number; // 송금완료 총액
  send_cnt: number;
  cnt: number; // 합계 건수
};

function formatDate(s?: string | null) {
  if (!s) return "-";
  try {
    const d = new Date(s);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return s || "-";
  }
}

function formatAmount(v?: number | null) {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Number(v).toLocaleString("ko-KR")}원`;
}

function formatWon(v?: number | null) {
  const n = Number(v || 0);
  return `${n.toLocaleString("ko-KR")}원`;
}

function buildFolderName(r: Row): string {
  const counterparty = cleanText(r.counterparty_name || "-");
  const category = cleanText(r.category || "-");
  const amount = Number(r.amount || 0);
  const amountText = amount > 0 ? `${amount.toLocaleString("ko-KR")}원` : "-";
  const applicant = cleanText(r.applicant_name || "-");
  const phone = cleanText(r.applicant_phone || "-");
  const email = cleanText(r.email || "-");
  return [counterparty, category, amountText, applicant, phone, email].join(" ").trim();
}

async function copyText(text: string) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "readonly");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}
/** ✅ 카테고리 라벨 (관리자 화면 표시용) */
const CATEGORY_KO: Record<string, string> = {
  rent: "임대료/월세",
  goods: "물품대금",
  salary: "급여",
  mgmt_fee: "관리비",
  maintenance: "관리비",
  rental: "렌탈료",

  // ✅ 추가: 주류페이 카테고리
  drink: "주류/음료",
};

const APPROVAL_LABELS = new Set([
  "승인대기",
  "결제대기",
  "결제완료",
  "송금완료",
  "요청철회/거절",
  "결제실패",
]);

function pickPillStatus(r: Row) {
  const a = (r.approval_status || "").trim();
  if (a && APPROVAL_LABELS.has(a)) return a;
  return r.status;
}

/** ✅ 줄바꿈/연속공백 제거 */
function cleanText(v: any): string {
  return String(v ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * ✅ "신청인" = 서비스 신청자(회원 이름)
 *  - counterparty / customer_name 계열은 절대 사용하지 않음
 */
function pickApplicantName(payment: any): string | null {
  const raw =
    payment?.applicant_name ??
    payment?.applicantName ??
    payment?.user_name ??
    payment?.userName ??
    payment?.requested_by_name ??
    payment?.requestedByName ??
    payment?.meta?.applicant_name ??
    payment?.meta?.requester_name ??
    null;

  const cleaned = cleanText(raw);
  return cleaned || null;
}

function pickApplicantPhone(payment: any): string | null {
  return (
    payment?.applicant_phone ??
    payment?.applicantPhone ??
    payment?.user_phone ??
    payment?.userPhone ??
    payment?.phone ??
    payment?.customer_phone ??
    payment?.customerPhone ??
    payment?.meta?.applicant_phone ??
    payment?.meta?.customer_phone ??
    null
  );
}

function pickApplicantEmail(payment: any): string | null {
  return (
    payment?.applicant_email ??
    payment?.applicantEmail ??
    payment?.email ??
    payment?.customer_email ??
    payment?.customerEmail ??
    payment?.meta?.applicant_email ??
    payment?.meta?.customer_email ??
    null
  );
}

/**
 * ✅ 상대방예금주(거래상대 수취인) 우선순위:
 * 1) API가 내려주는 counterparty_name / receiver / payee 류
 * 2) 없으면 title에서 "이체 결제(…)" 패턴만 벗겨서 내부만
 *
 * ⚠️ counterparty_holder는 "설정된 송금자명" 성격일 수 있어
 *    리스트의 "상대방예금주"에서는 우선순위에 넣지 않습니다.
 */
function pickCounterpartyName(payment: any): string | null {
  const direct =
    payment?.counterparty_name ??
    payment?.counterpartyName ??
    payment?.recipient_name ??
    payment?.recipientName ??
    payment?.receiver_name ??
    payment?.receiverName ??
    payment?.payee_name ??
    payment?.payeeName ??
    payment?.meta?.counterparty_name ??
    payment?.meta?.counterpartyName ??
    payment?.meta?.recipient_name ??
    payment?.meta?.receiver_name ??
    payment?.meta?.payee_name ??
    null;

  const directClean = cleanText(direct);
  if (directClean) return directClean;

  const titleRaw =
    payment?.title ?? payment?.payment_name ?? payment?.method ?? null;
  const t = cleanText(titleRaw);
  if (!t) return null;

  const m = t.match(/^\s*이체\s*결제\s*\((.+)\)\s*$/);
  if (m?.[1]) return cleanText(m[1]);

  return null;
}

const STATUS_OPTIONS = [
  "전체",
  "승인대기",
  "결제대기",
  "결제완료",
  "송금완료",
  "요청철회/거절",
  "결제실패",
];

/** ✅ KST 기준 YYYY-MM 추출 (관리자 PC 타임존과 무관하게 고정) */
function ymKST(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(d);

  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  if (!y || !m) return null;
  return `${y}-${m}`;
}

function num0(x: any): number {
  if (x == null) return 0;
  if (typeof x === "number") return Number.isFinite(x) ? x : 0;
  const v = Number(String(x).replace(/,/g, ""));
  return Number.isNaN(v) ? 0 : v;
}

/** ✅ 총액: total_amount 우선, 없으면 amount+fee+vat */
function getTotalAmount(p: any): number {
  const total = num0(p?.total_amount ?? p?.totalAmount ?? p?.meta?.total_amount);
  if (total > 0) return total;
  return (
    num0(p?.amount ?? p?.meta?.principal_amount) +
    num0(p?.fee_amount ?? p?.meta?.fee_amount) +
    num0(p?.vat_amount ?? p?.meta?.vat_amount)
  );
}

/**
 * ✅ "결제완료/송금완료" 판정
 * - approval_status 우선(한글)
 * - 일부 레거시: status가 complete인 경우 결제완료로 취급
 */
function classifyCompletion(p: any): "pay" | "send" | null {
  const a = String(p?.approval_status ?? p?.approvalStatus ?? "").trim();
  if (a === "송금완료") return "send";
  if (a === "결제완료") return "pay";

  const st = String(p?.status ?? p?.state ?? "").toLowerCase().trim();
  if (st === "complete" || st === "completed" || st === "paid") return "pay";

  return null;
}

/** ✅ 서버 수정 없이: 페이지 끝까지 전부 불러오기 */
async function fetchAllPayments(params: {
  q?: string;
  maxPages?: number;
  pageSize?: number;
}) {
  const q = params.q ?? "";
  const pageSize = Math.min(Math.max(Number(params.pageSize ?? 200), 50), 200);
  const maxPages = Math.min(Math.max(Number(params.maxPages ?? 500), 1), 2000);

  const all: any[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const res = await api.get("/admin/payments", {
      params: { q, page, page_size: pageSize },
    });

    const items = Array.isArray(res.data?.items)
      ? res.data.items
      : Array.isArray(res.data)
      ? res.data
      : [];

    if (!items.length) break;
    all.push(...items);

    // 마지막 페이지
    if (items.length < pageSize) break;
  }
  return all;
}

/** ✅ 연-월(YYYY-MM) 별 결제완료/송금완료 집계 */
function calcMonthlyAgg(all: any[]): MonthlyAgg[] {
  const map = new Map<
    string,
    { pay_total: number; pay_cnt: number; send_total: number; send_cnt: number }
  >();

  for (const p of all) {
    const kind = classifyCompletion(p);
    if (!kind) continue;

    // 완료월 기준: 결제완료는 payment_completed_at 우선, 그 외는 updated_at 우선
    const basis =
      (kind === "pay"
        ? p?.payment_completed_at ?? p?.paymentCompletedAt ?? null
        : null) ??
      p?.updated_at ??
      p?.updatedAt ??
      p?.created_at ??
      p?.createdAt ??
      null;
    const ym = ymKST(basis);
    if (!ym) continue;

    const amt = getTotalAmount(p);
    const cur =
      map.get(ym) || { pay_total: 0, pay_cnt: 0, send_total: 0, send_cnt: 0 };

    if (kind === "send") {
      cur.send_total += amt;
      cur.send_cnt += 1;
    } else {
      cur.pay_total += amt;
      cur.pay_cnt += 1;
    }
    map.set(ym, cur);
  }

  return Array.from(map.entries())
    .map(([ym, v]) => ({
      ym,
      pay_total: v.pay_total,
      pay_cnt: v.pay_cnt,
      send_total: v.send_total,
      send_cnt: v.send_cnt,
      cnt: v.pay_cnt + v.send_cnt,
    }))
    .sort((a, b) => (a.ym < b.ym ? -1 : 1));
}

export default function AdminPaymentRequests() {
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("전체");
  const [loading, setLoading] = useState(false);

  // ✅ 월별 집계(서버 무수정 버전)
  const [monthly, setMonthly] = useState<MonthlyAgg[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);

  // ✅ 명세서는 axios(baseURL)과 무관하게 "브라우저 새창"으로 여는 것이므로 /api 유지
  const openStatement = (id: number) => {
    const token = getToken();
    const url = token
      ? `/api/admin/payments/${id}/statement?token=${encodeURIComponent(token)}`
      : `/api/admin/payments/${id}/statement`;

    void openAuthenticatedUrl(url);
  };

  const copyFolderName = async (r: Row) => {
    const text = buildFolderName(r);
    try {
      await copyText(text);
      alert(`폴더명 복사 완료.\n${text}`);
    } catch (e) {
      console.error(e);
      alert("폴더명 복사에 실패했습니다.");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/payments", {
        params: { q, page: 1, page_size: 50, group_split: 1 },
      });

      const list = Array.isArray(res.data?.items)
        ? res.data.items
        : Array.isArray(res.data)
        ? res.data
        : [];

      const mapped: Row[] = list.map((p: any, idx: number) => {
        const payment = p;

        const id = Number(payment.id ?? idx + 1);
        const created_at: string = payment.created_at ?? "";
        const payment_completed_at: string | null =
          payment.payment_completed_at ?? payment.paymentCompletedAt ?? null;

        const status: string = String(
          payment.status ?? payment.state ?? "requested"
        ).toLowerCase();

        const approval_status: string | null =
          payment.approval_status ?? payment.approvalStatus ?? null;

        // amount normalize
        let amount: number | null = null;
        if (typeof payment.amount === "number") amount = payment.amount;
        else if (typeof payment.amount === "string") {
          const n = Number(String(payment.amount).replace(/,/g, ""));
          amount = Number.isNaN(n) ? null : n;
        }

        const rawCat: string | null =
          (payment.category as string | null) ?? null;

        let categoryText: string | null = null;
        if (rawCat && rawCat.trim()) {
          const key = rawCat.trim().toLowerCase();
          categoryText = CATEGORY_KO[key] || rawCat;
        }

        const applicant_name: string | null = pickApplicantName(payment);
        const applicant_phone: string | null = pickApplicantPhone(payment);
        const email: string | null = pickApplicantEmail(payment);

        const counterparty_name: string | null = pickCounterpartyName(payment);

        const split_count = Number(payment.split_count ?? payment.splitCount ?? 0) || 0;

        return {
          id,
          created_at,
          payment_completed_at,
          status,
          approval_status,
          counterparty_name,
          category: categoryText,
          applicant_name,
          applicant_phone,
          email,
          amount,
          split_count,
        };
      });

      setRows(mapped);
    } catch (e) {
      console.error(e);
      alert("결제 요청서를 불러오지 못했습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthly = async () => {
    setMonthlyLoading(true);
    setMonthlyError(null);
    try {
      // 검색어(q)를 반영한 집계를 원치 않으면 여기 q 대신 ""로 고정하면 됩니다.
      const all = await fetchAllPayments({ q, pageSize: 200, maxPages: 500 });
      const agg = calcMonthlyAgg(all);
      setMonthly(agg);
    } catch (e: any) {
      console.error(e);
      setMonthly([]);
      setMonthlyError("월별 집계에 실패했습니다.");
    } finally {
      setMonthlyLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadMonthly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load();
    loadMonthly();
  };

  const filteredRows = useMemo(() => {
    if (statusFilter === "전체") return rows;
    return rows.filter((r) => pickPillStatus(r) === statusFilter);
  }, [rows, statusFilter]);

  return (
    <div className="jp-admin-payments space-y-4">
      <div className="jp-admin-page-heading">
        <h1>결제 요청 관리</h1>
      </div>

      {/* ✅ 월별 결제완료/송금완료 집계 (연-월 기준) */}
      <div className="jp-admin-summary">
        <div className="jp-admin-summary-heading">
          <div className="space-y-0.5">
            <h2>월별 결제·송금 현황</h2>
          </div>

          <button
            type="button"
            onClick={loadMonthly}
            disabled={monthlyLoading}
            className={[
              "inline-flex h-9 items-center justify-center rounded-lg px-3 text-xs font-medium",
              monthlyLoading
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-slate-900 text-white hover:brightness-110",
            ].join(" ")}
            title="월별 집계 다시 계산"
          >
            {monthlyLoading ? "집계 중…" : "집계 새로고침"}
          </button>
        </div>

        {monthlyError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {monthlyError}
          </div>
        )}

        {monthly.slice(-1).map(m => <div className="jp-admin-month-cards" key={m.ym}><div><small>{m.ym} 결제완료</small><strong>{formatWon(m.pay_total)}</strong><span>{m.pay_cnt.toLocaleString("ko-KR")}건</span></div><div><small>{m.ym} 송금완료</small><strong>{formatWon(m.send_total)}</strong><span>{m.send_cnt.toLocaleString("ko-KR")}건</span></div><div><small>{m.ym} 합계</small><strong>{formatWon(m.pay_total + m.send_total)}</strong><span>{m.cnt.toLocaleString("ko-KR")}건</span></div></div>)}
        <details className="jp-admin-month-details"><summary>월별 상세 보기</summary><div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-sm">
            <thead className="text-slate-600">
              <tr className="border-b">
                <th className="py-2 text-left">연-월</th>
                <th className="py-2 text-right">결제완료(건)</th>
                <th className="py-2 text-right">결제완료(총액)</th>
                <th className="py-2 text-right">송금완료(건)</th>
                <th className="py-2 text-right">송금완료(총액)</th>
                <th className="py-2 text-right">합계(건)</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m) => (
                <tr key={m.ym} className="border-b border-slate-100">
                  <td className="py-2">{m.ym}</td>

                  <td className="py-2 text-right tabular-nums">
                    {m.pay_cnt.toLocaleString("ko-KR")}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatWon(m.pay_total)}
                  </td>

                  <td className="py-2 text-right tabular-nums">
                    {m.send_cnt.toLocaleString("ko-KR")}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatWon(m.send_total)}
                  </td>

                  <td className="py-2 text-right tabular-nums font-semibold">
                    {m.cnt.toLocaleString("ko-KR")}
                  </td>
                </tr>
              ))}

              {!monthly.length && !monthlyLoading && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500">
                    집계 데이터가 없습니다.
                  </td>
                </tr>
              )}

              {monthlyLoading && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500">
                    월별 집계 중…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div></details>
      </div>

      {/* 검색 + 상태필터 */}
      <form onSubmit={onSearch} className="jp-admin-filter-bar jp-admin-payment-search flex flex-col gap-2 md:flex-row">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="결제 요청 검색"
          placeholder="주문번호·예금주·신청인·이메일·연락처 검색"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm md:max-w-xl"
        />

        <select
          aria-label="결제 상태 필터"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm md:w-40"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === "전체" ? "상태 전체" : s}
            </option>
          ))}
        </select>

        <button
          type="submit"
          className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:brightness-110"
          disabled={loading}
        >
          검색
        </button>
      </form>
      <div className="jp-admin-list-meta"><span>조회 <strong>{rows.length}</strong>건 · 현재 표시 <strong>{filteredRows.length}</strong>건</span><span>상태 필터는 조회된 목록에 적용됩니다.</span></div>

      {/* =======================
          모바일: 카드 리스트
          ======================= */}
      <div className="jp-admin-responsive-cards mt-2">
        {loading && !filteredRows.length && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            불러오는 중…
          </div>
        )}

        {!loading && !filteredRows.length && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            결과가 없습니다.
          </div>
        )}

        {filteredRows.map((r) => {
          const pill = pickPillStatus(r);
          const canStatement = r.approval_status === "송금완료";
          const counterparty = r.counterparty_name || "-";

          return (
            <div
              key={r.id}
              className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-500">번호 {r.id}</div>
                <PaymentStatusPill status={pill as any} />
              </div>

              <div className="mt-1 text-[11px] text-slate-500">
                등록일자 {formatDate(r.created_at)}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                결제완료일 {formatDate(r.payment_completed_at)}
              </div>

              <div className="mt-3 h-px bg-slate-100" />

              <div className="mt-3 space-y-1.5 text-xs text-slate-800">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">상대방예금주</span>
                  <span className="flex-1 text-right font-medium break-words">
                    {counterparty}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">카테고리</span>
                  <span className="flex-1 text-right">{r.category || "-"}</span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">요청 금액</span>
                  <span className="flex-1 text-right tabular-nums">
                    {formatAmount(r.amount)}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">신청인</span>
                  <span className="flex flex-1 items-center justify-end gap-1.5 text-right">
                    <span>{r.applicant_name || "-"}</span>
                    {Number(r.split_count || 0) > 1 && (
                      <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-sm font-medium leading-none text-amber-700">
                        분할 {Number(r.split_count)}건
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">연락처</span>
                  <span className="flex-1 text-right tabular-nums">
                    {r.applicant_phone || "-"}
                  </span>
                </div>

                {r.email && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">이메일</span>
                    <span className="min-w-0 flex-1 break-all text-right">{r.email}</span>
                  </div>
                )}
              </div>

              <div className="jp-admin-mobile-actions mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => copyFolderName(r)}
                  className="inline-flex h-8 items-center rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                >
                  폴더명 복사
                </button>
                <button
                  type="button"
                  onClick={() => nav(`/admin/payment-requests/${r.id}`)}
                  className="inline-flex h-8 items-center rounded-lg border border-slate-300 px-3 text-xs font-medium text-slate-800 hover:bg-slate-50"
                >
                  상세 보기
                </button>

                <button
                  type="button"
                  onClick={() => openStatement(r.id)}
                  disabled={!canStatement}
                  className={[
                    "inline-flex h-8 items-center rounded-lg px-3 text-xs font-medium",
                    canStatement
                      ? "bg-slate-900 text-white hover:brightness-110"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed",
                  ].join(" ")}
                  title={
                    canStatement
                      ? "거래명세서 보기"
                      : "송금완료 상태에서만 발행됩니다."
                  }
                >
                  명세서
                </button>
              </div>
            </div>
          );
        })}
      </div>


      {/* =======================
          데스크톱: 테이블
          ======================= */}
      <div className="jp-admin-responsive-table mt-2 rounded-xl border border-slate-200 bg-white">
        <table className="jp-admin-data-table"><thead><tr><th className="text-left">요청</th><th className="text-left">진행 상태</th><th className="text-left">거래처</th><th className="text-right">요청 금액</th><th className="text-left">신청인</th><th className="text-center">관리</th></tr></thead><tbody>
          {filteredRows.map(r => {
            const pill = pickPillStatus(r);
            const canStatement = r.approval_status === "송금완료";
            const counterparty = r.counterparty_name || "-";
            return <tr key={r.id} className="border-t border-slate-100">
              <td><strong>#{r.id}</strong><small>등록 {formatDate(r.created_at)}</small><small>결제 {formatDate(r.payment_completed_at)}</small></td>
              <td><PaymentStatusPill status={pill} />{Number(r.split_count || 0) > 1 && <small>분할 {Number(r.split_count)}건</small>}</td>
              <td className="break-words" title={counterparty}><strong>{counterparty}</strong><small>{r.category || "-"}</small></td>
              <td className="text-right"><strong className="jp-admin-amount">{formatAmount(r.amount)}</strong></td>
              <td className="break-all"><strong>{r.applicant_name || "-"}</strong><small>{r.applicant_phone || "-"}</small><small>{r.email || "-"}</small></td>
              <td><div className="jp-admin-table-actions"><button type="button" onClick={() => copyFolderName(r)} aria-label="폴더명 복사" title="폴더명 복사" className="border border-slate-200 bg-white text-slate-600"><Copy size={15} aria-hidden="true" /></button><button type="button" onClick={() => openStatement(r.id)} disabled={!canStatement} aria-label="거래명세서 보기" title={canStatement ? "거래명세서 보기" : "송금완료 상태에서만 발행됩니다."} className="border border-slate-200 bg-white text-slate-600 disabled:opacity-40"><FileText size={15} aria-hidden="true" /></button><button type="button" onClick={() => nav(`/admin/payment-requests/${r.id}`)} className="jp-admin-detail-link">상세 ↗</button></div></td>
            </tr>;
          })}
          {!filteredRows.length && !loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">결과가 없습니다.</td></tr>}
          {loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">불러오는 중…</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}
